import PersonIcon from "@mui/icons-material/Person";
import { Avatar, Badge, Box, ListItemAvatar, ListItemButton, ListItemText, Typography } from "@mui/material";
import { colors } from "../../../../theme/muiColors";
import { ContactPresenceDot } from "../../../internal-contacts/_components/ContactPresenceDot";
import { Contact } from "../../../internal-contacts/_types/internalContacts.types";
import { ContactPersonStatus } from "../../../internal-contacts/_types/workforcePresence.types";
import { getContactTitle } from "../../../internal-contacts/_utils/internalContacts.utils";

const formatConversationDate = (date?: string | null) => {
  if (!date) return "";

  const value = new Date(date);
  const now = new Date();

  const isToday = value.toDateString() === now.toDateString();

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = value.toDateString() === yesterday.toDateString();

  if (isToday) return value.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (isYesterday) return "Yesterday";

  return value.toLocaleDateString([], { month: "short", day: "numeric" });
};

interface InternalContactListItemProps {
  contact: Contact & {
    lastMessage?: string;
    lastMessageDate?: string;
    lastMessageDirection?: "incoming" | "outgoing";
  };
  isSelected: boolean;
  unreadCount: number;
  onSelect: (contact: Contact) => void;
  personStatus?: ContactPersonStatus | null;
}

export function InternalContactListItem({ contact, isSelected, unreadCount, onSelect, personStatus }: InternalContactListItemProps) {
  const title = getContactTitle(contact);

  const infoLine = [contact.companyName, contact.email].filter(Boolean).join(" · ");
  const hasUnread = unreadCount > 0;
  const isCompany = contact.type === "company";
  const lastMessageDate = formatConversationDate(contact.lastMessageDate);

  const getLastMessagePreview = () => {
    if (!contact.lastMessage) return "";
    if (contact.lastMessageDirection === "outgoing") {
      return `You: ${contact.lastMessage}`;
    }
    return contact.lastMessage;
  };

  const lastMessagePreview = getLastMessagePreview();

  return (
    <ListItemButton
      onClick={() => onSelect(contact)}
      sx={{
        py: 1.15,
        px: 1.5,
        mb: 0.6,
        borderRadius: "14px",
        backgroundColor: isSelected ? colors.blue[50] : "transparent",
        transition: "all 0.2s",
        alignItems: "flex-start",
        "&:hover": {
          bgcolor: isSelected ? colors.blue[50] : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.035)",
        },
      }}
    >
      <ListItemAvatar sx={{ minWidth: 45, pt: 0.2 }}>
        <Badge color="error" variant="dot" invisible={!hasUnread} overlap="circular" anchorOrigin={{ vertical: "top", horizontal: "right" }}>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <Avatar
              src={!isCompany ? contact.avatar || undefined : undefined}
              sx={{
                width: 34,
                height: 34,
                bgcolor: isSelected ? colors.blue[500] : colors.blue[50],
                color: isSelected ? "white" : colors.blue[600],
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {(!contact.avatar || isCompany) && (title[0] || <PersonIcon sx={{ fontSize: 16 }} />)}
            </Avatar>
            <ContactPresenceDot contactType={contact.type} personStatus={personStatus} />
          </Box>
        </Badge>
      </ListItemAvatar>

      <ListItemText
        sx={{ my: 0, minWidth: 0 }}
        primary={
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Typography
              component="span"
              noWrap
              sx={{
                flex: 1,
                fontSize: "14px",
                fontWeight: hasUnread ? 800 : 600,
                color: isSelected ? colors.blue[900] : "text.primary",
              }}
            >
              {title}
            </Typography>
            {lastMessageDate ? (
              <Typography
                component="span"
                sx={{
                  fontSize: "11px",
                  color: hasUnread ? colors.blue[700] : "text.secondary",
                  whiteSpace: "nowrap",
                  fontWeight: hasUnread ? 700 : 400,
                }}
              >
                {lastMessageDate}
              </Typography>
            ) : null}
          </Box>
        }
        secondary={
          <Box component="span" sx={{ minWidth: 0, display: "block" }}>
            {infoLine ? (
              <Typography
                component="span"
                noWrap
                sx={{
                  display: "block",
                  fontSize: "12px",
                  color: "text.secondary",
                  lineHeight: 1.35,
                }}
              >
                {infoLine}
              </Typography>
            ) : null}
            {lastMessagePreview ? (
              <Typography
                component="span"
                noWrap
                sx={{
                  display: "block",
                  fontSize: "12px",
                  color: hasUnread ? "text.primary" : "text.secondary",
                  fontWeight: hasUnread ? 600 : 400,
                  lineHeight: 1.35,
                  mt: infoLine ? 0.2 : 0,
                }}
              >
                {lastMessagePreview}
              </Typography>
            ) : null}
          </Box>
        }
      />

      {hasUnread ? (
        <Box
          sx={{
            minWidth: 20,
            height: 20,
            px: 0.7,
            borderRadius: "999px",
            bgcolor: colors.blue[500],
            color: "#fff",
            fontSize: "11px",
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ml: 1,
            mt: 2.3,
          }}
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </Box>
      ) : null}
    </ListItemButton>
  );
}
